import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  campaignService,
  EntityService,
  myDesignService,
} from "../../services";
import { Container, FormControlLabel, Switch } from "@mui/material";
import { TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Edit, Search } from "@mui/icons-material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import CreateCampaignForm from "../../components/forms/CreateCampaignForm";
import CampaignsParticipants from "../../components/participants/CampaignsParticipants";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import GroupIcon from "@mui/icons-material/Group";

const PREFIX = "CampaignManagement";

const classes = {
  buttonSpaces: `${PREFIX}-buttonSpaces`,
  marginBottom: `${PREFIX}-marginBottom`,
  filter: `${PREFIX}-filter`,
  filterButtons: `${PREFIX}-filterButtons`,
  createButton: `${PREFIX}-createButton`,
  mobileMarginBottom: `${PREFIX}-mobileMarginBottom`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.buttonSpaces}`]: {
    backgroundColor: "#424242",
    color: theme.palette.primary.main,
    marginRight: "15px",
    "&:hover": {
      backgroundColor: "rgba(66,66,66,0.7)",
      color: theme.palette.primary.main,
    },
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: "0.5rem",
  },

  [`& .${classes.filter}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`& .${classes.filterButtons}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "1rem",
    marginTop: "1rem",
  },

  [`& .${classes.createButton}`]: {
    position: "absolute",
    left: 0,
    marginLeft: "1rem",
  },

  [`&.${classes.mobileMarginBottom}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      marginBottom: "5rem",
    },
  },
}));

const CampaignManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [selectedCampaign, setSelectedCampaign] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [campaigns, setCampaigns] = React.useState([]);
  const [target, setTarget] = React.useState(null);
  const [entities, setEntities] = React.useState(null);
  const [name, setName] = React.useState("");
  const [isParticipants, setIsParticipants] = React.useState(null);

  const handleStatus = (value) => {
    switch (value) {
      case "NOT_STARTED":
        return t("campaignsManagement.grid.statusNotStarted");
      case "ON_GOING":
        return t("campaignsManagement.grid.statusOnGoing");
      case "TERMINATED":
        return t("campaignsManagement.grid.statusTerminated");
      case "ARCHIVED":
        return t("campaignsManagement.grid.statusArchived");
      default:
        return t("campaignsManagement.grid.statusUnknown");
    }
  };

  const columns = [
    {
      field: "state",
      headerName: t("campaignsManagement.grid.status"),
      width: 120,
      renderCell: (params) => handleStatus(params.value),
    },
    {
      field: "targetLabel",
      headerName: t("campaignsManagement.grid.targetName"),
      width: 170,
    },
    {
      field: "countFan",
      headerName: t("campaignsManagement.grid.fanNumber"),
      width: 120,
    },
    {
      field: "countVip",
      headerName: t("campaignsManagement.grid.vipNumber"),
      width: 120,
    },
    {
      field: "name",
      headerName: t("campaignsManagement.grid.name"),
      width: 130,
    },
    {
      field: "action",
      width: 130,
      headerName: t("campaignsManagement.grid.actions"),
      renderCell: (params) => (
        <div style={{ textAlign: "center" }}>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => editCampaign(params.row)}
          >
            <Edit />
          </IconButton>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => getParticipants(params.row)}
          >
            <GroupIcon />
          </IconButton>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleVisibleChange(params.row)}
          >
            {params.row.visible ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </div>
      ),
    },
    {
      field: "published",
      headerName: t("myDesign.campaigns.publishedColumn"),
      width: 150,
      renderCell: (params) => (
        <div>
          <FormControlLabel
            control={
              <Switch
                checked={params.row.published === 1 ? true : false}
                onChange={() => handlePublishedChange(params.row)}
                name="published"
                color="primary"
              />
            }
          />
        </div>
      ),
    },
    {
      field: "targetUrl",
      headerName: t("campaignsManagement.grid.targetUrl"),
      width: 260,
    },
    // {field: 'startDate', headerName: t('campaignsManagement.grid.startDate'), width: 140, valueGetter: (params) => params.value.split('T')[0]},
    // {field: 'endDate', headerName: t('campaignsManagement.grid.endDate'), width: 140, valueGetter: (params) => params.value.split('T')[0]},
    {
      field: "type",
      headerName: t("campaignsManagement.grid.type"),
      width: 110,
    },
    {
      field: "codeVip",
      headerName: t("campaignsManagement.grid.vipCode"),
      width: 140,
    },
    {
      field: "cost",
      headerName: t("campaignsManagement.grid.price"),
      width: 120,
    },
  ];

  const handleVisibleChange = (item) => {
    myDesignService
      .updateCampaignVisible(item.id, item.visible === 1 ? false : true)
      .then(() => {
        enqueueSnackbar(t("toasts.updateCampaignSuccess"), {
          variant: "success",
        });
        loadData();
      });
  };

  const handlePublishedChange = (item) => {
    myDesignService
      .updateCampaignPublish(item.id, item.published === 1 ? false : true)
      .then(() => {
        enqueueSnackbar(t("toasts.updateCampaignSuccess"), {
          variant: "success",
        });
        loadData();
      });
  };

  useEffect(() => {
    loadData();
    EntityService.getEntities("sphere").then((result) => {
      setEntities(result.data);
    });
  }, []);

  const getParticipants = (row) => {
    setIsParticipants(row);
    setTarget({
      frameId: row.frameTargetId,
      label: row.targetLabel,
      id: row.id,
    });
  };

  const editCampaign = (row) => {
    setSelectedCampaign(row);
    setTarget({
      frameId: row.frameTargetId,
      label: row.targetLabel,
      id: row.id,
    });
  };

  const loadData = () => {
    setLoading(true);
    campaignService
      .fetchCampaignList()
      .then((result) => {
        setCampaigns(result.reverse());
      })
      .finally(() => setLoading(false));
  };

  const search = () => {
    let tmp = [...campaigns];
    const results = tmp.filter(
      (campaign) =>
        campaign.name.toUpperCase().includes(name.toUpperCase()) ||
        campaign.targetLabel.toUpperCase().includes(name.toUpperCase())
    );
    setCampaigns(results);
  };

  const reinit = async () => {
    await setName(() => "");
    loadData();
  };

  return (
    <StyledDiv>
      <Container className={classes.mobileMarginBottom}>
        <h1>{t("campaignsManagement.title")}</h1>
        <div>
          <h4 className={classes.marginBottom}>
            {t("campaignsManagement.filters")}
          </h4>
          <div className={classes.filter}>
            <TextField
              variant="outlined"
              margin="normal"
              id="name"
              label={t("campaignsManagement.name")}
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className={classes.filterButtons}>
            <Button onClick={search} variant="contained" color="primary">
              <Search />
              {t("campaignsManagement.search")}
            </Button>
            <Button
              onClick={reinit}
              variant="contained"
              style={{ marginLeft: "1rem" }}
            >
              {t("campaignsManagement.reinit")}
            </Button>
          </div>
        </div>
        <DataGrid
          rows={campaigns}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          loading={loading}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[5, 10, 25, 50]}
        />
        <ResponsiveDialogManager
          onClose={() => setSelectedCampaign(null)}
          open={selectedCampaign && target ? true : false}
          renderer={
            <CreateCampaignForm
              selectedCampaign={selectedCampaign}
              selectedTarget={target}
              entities={entities}
              setCreateCampaign={() => {
                setTarget(null);
                setSelectedCampaign(null);
              }}
            />
          }
          title={t("campaignsManagement.modifyCampaign")}
        />
        <ResponsiveDialogManager
          onClose={() => {
            setIsParticipants(null);
            setTarget(null);
          }}
          fullScreen
          open={isParticipants && target ? true : false}
          renderer={
            <CampaignsParticipants target={target} reloadData={loadData} />
          }
          title={t("campaignsManagement.campaignParticipants")}
        />
      </Container>
    </StyledDiv>
  );
};

export default CampaignManagement;
